// 016 — Atención Inmediata al Recién Nacido (Fase 5, Tanda 2, ver PLAN.md).
// Transcripción campo por campo del PDF oficial (páginas 68-69, footer
// `SNS-MSP/HCU-form.016/2021`, instructivo "15 Bloques" A-O). Antes solo
// cubría Apgar (score final), peso/talla/PC al nacer y 1 patología.
//
// Criterios ya usados en 113/114/115 y acá:
// - Apgar: grilla real de 5 aspectos × 0/1/2 por minuto (1/5/10/15/20), el
//   total lo calcula el código (`EscalaClinica`), nunca texto libre; sin
//   bandas de interpretación porque el papel no imprime ninguna.
// - El resto son campos simples/tablas (`TablaConColumnas`) y quedan como
//   `Par[]` de respaldo; solo lo que ya tenía consumidor FHIR real lo sigue
//   teniendo (Procedure, Apgar 1/5/10, peso/talla/PC iniciales, Condition por
//   diagnóstico inicial) — sin recursos nuevos sin consumidor demostrable.
// - "% pérdida de peso" se calcula (inicial vs egreso), no se tipea.
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import type { DiagnosticoConMarca } from './MarcaDiagnostico';
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca } from './MarcaDiagnostico';
import { conCodigoCie, MARCAS_PRE_DEF, paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import type { Par } from './index';
import { CAMPOS_PROFESIONAL_PAPEL, estadoInicialProfesionalMultiple, paresProfesionalPorTurno, type EstadoProfesionalMultiple } from './profesionalPorTurnoDatos';
import type { Traducir } from './tecnicaAnestesicaDatos';
import { calcularTotalEscala, type DefinicionEscalaClinica, type RespuestasEscalaClinica } from './uci/EscalaClinica';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';

// ---------- B. Antecedentes patológicos familiares ----------

export const CAMPOS_FAMILIARES_016: readonly CampoSimple[] = [
  { clave: 'madre', etiqueta: 'Madre' },
  { clave: 'padre', etiqueta: 'Padre' },
  { clave: 'otrosFamiliares', etiqueta: 'Otros familiares' },
];

export const NUM_HERMANOS_016 = 6;
export const OPCIONES_TIPO_PARTO_HERMANO_016 = ['Céfalo vaginal', 'Cesárea', 'Podálico vaginal'] as const;
export const OPCIONES_CONDICION_PARTO_HERMANO_016 = ['Pre término', 'A término', 'Post término'] as const;

export interface FilaHermano016 {
  readonly tipoParto: string;
  readonly condicionParto: string;
  readonly edadActual: string;
  readonly observaciones: string;
}
export type EstadoHermanos016 = Readonly<Record<string, FilaHermano016>>;

export function estadoInicialHermanos016(): EstadoHermanos016 {
  return Object.fromEntries(Array.from({ length: NUM_HERMANOS_016 }, (_, i) => [String(i + 1), { tipoParto: '', condicionParto: '', edadActual: '', observaciones: '' }]));
}

export const CAMPOS_OBSTETRICOS_MADRE_016: readonly CampoSimple[] = [
  { clave: 'gestas', etiqueta: 'Número de gestas', tipo: 'numero' },
  { clave: 'abortos', etiqueta: 'Número de abortos', tipo: 'numero' },
  { clave: 'partos', etiqueta: 'Número de partos', tipo: 'numero' },
  { clave: 'cesareas', etiqueta: 'Número de cesáreas', tipo: 'numero' },
  { clave: 'hijosVivos', etiqueta: 'Número de hijos vivos', tipo: 'numero' },
  { clave: 'hijosMuertos', etiqueta: 'Número de hijos muertos', tipo: 'numero' },
];

// ---------- D. Antecedentes natales ----------

export const CAMPOS_NATALES_016: readonly CampoSimple[] = [
  { clave: 'horasHidrorrea', etiqueta: 'Horas de hidrorrea', tipo: 'numero' },
  { clave: 'numeroTactos', etiqueta: 'Número de tactos posthidrorrea', tipo: 'numero' },
  { clave: 'laborHoras', etiqueta: 'Labor (en horas)', tipo: 'numero' },
  { clave: 'expulsivoMinutos', etiqueta: 'Expulsivo (en minutos)', tipo: 'numero' },
  { clave: 'liquidoCantidad', etiqueta: 'Líquido amniótico — cantidad' },
  { clave: 'liquidoColor', etiqueta: 'Líquido amniótico — color' },
  { clave: 'liquidoOlor', etiqueta: 'Líquido amniótico — olor' },
  { clave: 'liquidoCaracteristicas', etiqueta: 'Líquido amniótico — características' },
  { clave: 'fechaNacimiento', etiqueta: 'Fecha de nacimiento', tipo: 'fecha' },
  { clave: 'horaNacimiento', etiqueta: 'Hora de nacimiento', tipo: 'hora' },
  { clave: 'fum', etiqueta: 'Edad gestacional por: fecha última menstruación', tipo: 'fecha' },
  { clave: 'ecoExtrapolado', etiqueta: 'Edad gestacional por: eco extrapolado (semanas)' },
  { clave: 'capurro', etiqueta: 'Edad gestacional por: Capurro (semanas)' },
  { clave: 'ballard', etiqueta: 'Edad gestacional por: Ballard (semanas)' },
];

// ---------- SI/NO: sufrimiento fetal (D), F intervenciones, G profilaxis, H vacunas ----------

export interface GrupoSiNo016 {
  readonly clave: string;
  readonly items: readonly { readonly clave: string; readonly etiqueta: string }[];
}

export const GRUPOS_SI_NO_016: readonly GrupoSiNo016[] = [
  { clave: 'natales', items: [{ clave: 'sufrimientoFetal', etiqueta: 'Sufrimiento fetal' }] },
  {
    clave: 'intervenciones',
    items: [
      { clave: 'reanimacion', etiqueta: 'Reanimación' },
      { clave: 'estimulacion', etiqueta: 'Estimulación' },
      { clave: 'aspiracion', etiqueta: 'Aspiración' },
      { clave: 'mascara', etiqueta: 'Máscara' },
      { clave: 'bolsa', etiqueta: 'Bolsa' },
      { clave: 'tuboEndotraqueal', etiqueta: 'Tubo endotraqueal' },
      { clave: 'masajeCardiaco', etiqueta: 'Masaje cardíaco' },
      { clave: 'medicacion', etiqueta: 'Medicación' },
    ],
  },
  {
    clave: 'profilaxis',
    items: [
      { clave: 'vitaminaK', etiqueta: 'Vitamina K' },
      { clave: 'ocular', etiqueta: 'Profilaxis ocular' },
    ],
  },
  {
    clave: 'vacunas',
    items: [
      { clave: 'hepatitisB', etiqueta: 'Hepatitis B' },
      { clave: 'bcg', etiqueta: 'BCG' },
    ],
  },
];

export type ValorSiNo = '' | 'si' | 'no';
export type EstadoSiNo016 = Readonly<Record<string, ValorSiNo>>;

export function estadoInicialSiNo016(): EstadoSiNo016 {
  return Object.fromEntries(GRUPOS_SI_NO_016.flatMap((g) => g.items.map((i) => [i.clave, '' as ValorSiNo])));
}

// ---------- E. Apgar ----------

export const MINUTOS_APGAR_016 = ['1', '5', '10', '15', '20'] as const;
export type MinutoApgar016 = (typeof MINUTOS_APGAR_016)[number];

export const DEFINICION_APGAR_016: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'frecuenciaCardiaca',
      etiqueta: 'Frecuencia cardíaca',
      opciones: [
        { valor: 0, etiqueta: 'No hay' },
        { valor: 1, etiqueta: 'Menos de 100' },
        { valor: 2, etiqueta: '100 o más' },
      ],
    },
    {
      clave: 'esfuerzoRespiratorio',
      etiqueta: 'Esfuerzo respiratorio',
      opciones: [
        { valor: 0, etiqueta: 'No hay, no respira' },
        { valor: 1, etiqueta: 'Llanto débil, respira mal' },
        { valor: 2, etiqueta: 'Llanto vigoroso' },
      ],
    },
    {
      clave: 'tonoMuscular',
      etiqueta: 'Tono muscular',
      opciones: [
        { valor: 0, etiqueta: 'Flacidez' },
        { valor: 1, etiqueta: 'Flexión parcial de extremidades' },
        { valor: 2, etiqueta: 'Movimientos activos, buena flexión' },
      ],
    },
    {
      clave: 'irritabilidad',
      etiqueta: 'Irritabilidad o respuesta a la estimulación',
      opciones: [
        { valor: 0, etiqueta: 'No reacciona' },
        { valor: 1, etiqueta: 'Mueca, succión, algún movimiento por estímulo' },
        { valor: 2, etiqueta: 'Tos o estornudo, llanto, reacciona defendiéndose' },
      ],
    },
    {
      clave: 'colorPiel',
      etiqueta: 'Color de la piel',
      opciones: [
        { valor: 0, etiqueta: 'Cianosis o palidez' },
        { valor: 1, etiqueta: 'Pies o manos cianóticas' },
        { valor: 2, etiqueta: 'Completamente rosado' },
      ],
    },
  ],
  rangos: [],
};

export type EstadoApgar016 = Readonly<Record<MinutoApgar016, RespuestasEscalaClinica>>;

export function estadoInicialApgar016(): EstadoApgar016 {
  return { '1': {}, '5': {}, '10': {}, '15': {}, '20': {} };
}

export function totalApgar016(estado: EstadoApgar016, minuto: MinutoApgar016): number | undefined {
  return calcularTotalEscala(DEFINICION_APGAR_016, estado[minuto]);
}

// ---------- I. Constantes vitales ----------

export const NUM_FILAS_CONSTANTES_016 = 4;
export const FILAS_CONSTANTES_016: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_CONSTANTES_016 }, (_, i) => ({ clave: String(i + 1), etiqueta: String(i + 1) }));
export const COLUMNAS_CONSTANTES_016: readonly ColumnaTablaConColumnas[] = [
  { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
  { clave: 'hora', etiqueta: 'Hora' },
  { clave: 'temperatura', etiqueta: 'Temperatura °C' },
  { clave: 'pulso', etiqueta: 'Pulso/min' },
  { clave: 'presionArterial', etiqueta: 'Presión arterial (mmHg)' },
  { clave: 'frecuenciaRespiratoria', etiqueta: 'Frecuencia respiratoria/min' },
  { clave: 'pulsioximetria', etiqueta: 'Pulsioximetría (%)' },
  { clave: 'reflejoSuccion', etiqueta: 'Reflejo succión' },
  { clave: 'toleranciaOral', etiqueta: 'Tolerancia oral' },
  { clave: 'deposicion', etiqueta: 'Deposición' },
  { clave: 'orina', etiqueta: 'Orina' },
  { clave: 'responsable', etiqueta: 'Responsable' },
];

// ---------- J. Examen físico ----------

export const SISTEMAS_EXAMEN_016: readonly { readonly clave: string; readonly etiqueta: string }[] = [
  { clave: 'piel', etiqueta: 'Piel' },
  { clave: 'cabeza', etiqueta: 'Cabeza' },
  { clave: 'fontanela', etiqueta: 'Fontanela' },
  { clave: 'ojos', etiqueta: 'Ojos' },
  { clave: 'oidos', etiqueta: 'Oídos' },
  { clave: 'bocaNariz', etiqueta: 'Boca / nariz' },
  { clave: 'cuello', etiqueta: 'Cuello' },
  { clave: 'torax', etiqueta: 'Tórax' },
  { clave: 'pulmones', etiqueta: 'Pulmones' },
  { clave: 'corazon', etiqueta: 'Corazón' },
  { clave: 'abdomen', etiqueta: 'Abdomen' },
  { clave: 'cordonUmbilical', etiqueta: 'Cordón umbilical' },
  { clave: 'genitales', etiqueta: 'Genitales' },
  { clave: 'ano', etiqueta: 'Ano' },
  { clave: 'columna', etiqueta: 'Columna' },
  { clave: 'extremidades', etiqueta: 'Extremidades' },
  { clave: 'neurologico', etiqueta: 'Neurológico' },
];

export const OPCIONES_ESTADO_SISTEMA_016 = [
  { valor: 'sp', etiqueta: 'SP — sin patología' },
  { valor: 'cp', etiqueta: 'CP — con patología' },
] as const;

export interface FilaSistema016 {
  readonly inicialEstado: string;
  readonly inicialDescripcion: string;
  readonly egresoEstado: string;
  readonly egresoDescripcion: string;
}
export type EstadoSistemas016 = Readonly<Record<string, FilaSistema016>>;

export function estadoInicialSistemas016(): EstadoSistemas016 {
  return Object.fromEntries(SISTEMAS_EXAMEN_016.map((s) => [s.clave, { inicialEstado: '', inicialDescripcion: '', egresoEstado: '', egresoDescripcion: '' }]));
}

export const CAMPOS_ANTROPOMETRIA_016: readonly CampoSimple[] = [
  { clave: 'inicialPeso', etiqueta: 'Inicial — peso (kg)', tipo: 'numero' },
  { clave: 'inicialLongitud', etiqueta: 'Inicial — longitud (cm)', tipo: 'numero' },
  { clave: 'inicialPerimetroCefalico', etiqueta: 'Inicial — perímetro cefálico (cm)', tipo: 'numero' },
  { clave: 'egresoPeso', etiqueta: 'Al egreso — peso (kg)', tipo: 'numero' },
  { clave: 'egresoLongitud', etiqueta: 'Al egreso — longitud (cm)', tipo: 'numero' },
  { clave: 'egresoPerimetroCefalico', etiqueta: 'Al egreso — perímetro cefálico (cm)', tipo: 'numero' },
];

export const CAMPOS_SATURACION_016: readonly CampoSimple[] = [
  { clave: 'inicialPreductal', etiqueta: 'Inicial 24 horas — SatO2 preductal', tipo: 'numero' },
  { clave: 'inicialPostductal', etiqueta: 'Inicial 24 horas — SatO2 postductal', tipo: 'numero' },
  { clave: 'egresoPreductal', etiqueta: 'Egreso — SatO2 preductal', tipo: 'numero' },
  { clave: 'egresoPostductal', etiqueta: 'Egreso — SatO2 postductal', tipo: 'numero' },
];

function numeroValido(texto: string | undefined): number | undefined {
  if (texto === undefined || texto.trim() === '') return undefined;
  const n = Number(texto);
  return Number.isNaN(n) ? undefined : n;
}

/** `(inicial − egreso) / inicial × 100`, calculado — `undefined` si falta alguno o el peso inicial no es positivo. Un valor negativo es ganancia de peso, se muestra tal cual. */
export function porcentajePerdidaPeso016(antropometria: Valores): number | undefined {
  const inicial = numeroValido(antropometria.inicialPeso);
  const egreso = numeroValido(antropometria.egresoPeso);
  if (inicial === undefined || egreso === undefined || inicial <= 0) return undefined;
  return ((inicial - egreso) / inicial) * 100;
}

// ---------- K / M. Diagnósticos ----------

export const NUM_DIAGNOSTICOS_INICIAL_016 = 6;
export const NUM_DIAGNOSTICOS_EGRESO_016 = 4;

// ---------- O. Profesional responsable ----------

// El papel (hoja 016 (2), bloque O) trae DOS profesionales, INICIAL y EGRESO, cada uno con fecha, hora, nombres, apellidos y número de
// documento. Un documento lo firma UNA persona: el servidor arma los datos de quien firma (marca `MARCA_FIRMANTE_SERVIDOR`) y el otro
// profesional se captura como texto estructurado — mismo mecanismo que 113 K / 114 M (`profesionalPorTurnoDatos.ts`, T6b 2026-09-24).
export const TURNOS_PROFESIONAL_016 = ['inicial', 'egreso'] as const;
export type TurnoProfesional016 = (typeof TURNOS_PROFESIONAL_016)[number];

// ---------- Estado completo ----------

export interface EstadoBitacora016 {
  readonly familiares: Valores;
  readonly hermanos: EstadoHermanos016;
  readonly obstetricosMadre: Valores;
  readonly antecedentesPrenatales: string;
  readonly natales: Valores;
  readonly siNo: EstadoSiNo016;
  readonly apgar: EstadoApgar016;
  readonly constantes: EstadoTablaConColumnas;
  readonly sistemas: EstadoSistemas016;
  readonly antropometria: Valores;
  readonly saturacion: Valores;
  readonly diagnosticosInicial: readonly DiagnosticoConMarca[];
  readonly planInicial: string;
  readonly clasificacionNeonato: string;
  readonly diagnosticosEgreso: readonly DiagnosticoCie10Valor[];
  readonly planEgreso: string;
  /** O. Profesional responsable (inicial y egreso); el que firma lo arma el servidor. */
  readonly profesional: EstadoProfesionalMultiple<TurnoProfesional016>;
}

export function estadoInicialBitacora016(): EstadoBitacora016 {
  return {
    familiares: valoresVacios(CAMPOS_FAMILIARES_016),
    hermanos: estadoInicialHermanos016(),
    obstetricosMadre: valoresVacios(CAMPOS_OBSTETRICOS_MADRE_016),
    antecedentesPrenatales: '',
    natales: valoresVacios(CAMPOS_NATALES_016),
    siNo: estadoInicialSiNo016(),
    apgar: estadoInicialApgar016(),
    constantes: estadoInicialTablaConColumnas(FILAS_CONSTANTES_016, COLUMNAS_CONSTANTES_016),
    sistemas: estadoInicialSistemas016(),
    antropometria: valoresVacios(CAMPOS_ANTROPOMETRIA_016),
    saturacion: valoresVacios(CAMPOS_SATURACION_016),
    diagnosticosInicial: Array.from({ length: NUM_DIAGNOSTICOS_INICIAL_016 }, () => diagnosticoConMarcaVacio()),
    planInicial: '',
    clasificacionNeonato: '',
    diagnosticosEgreso: Array.from({ length: NUM_DIAGNOSTICOS_EGRESO_016 }, () => ({ texto: '', cie10: null })),
    planEgreso: '',
    profesional: estadoInicialProfesionalMultiple(TURNOS_PROFESIONAL_016),
  };
}

// ---------- Par[] de respaldo ----------

function paresDeFilasConColumnas(filas: readonly FilaTablaConColumnas[], columnas: readonly ColumnaTablaConColumnas[], estado: EstadoTablaConColumnas, titulo: string): Par[] {
  return filas.map((f) => ({ pregunta: `${titulo} — ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(estado, f.clave, columnas) }));
}

function etiquetaSiNo(valor: ValorSiNo): string {
  return valor === 'si' ? 'Sí' : valor === 'no' ? 'No' : '';
}

/** Serializa toda la bitácora de 016 a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeBitacora016(t: Traducir, estado: EstadoBitacora016): Par[] {
  const pares: Par[] = [];

  pares.push(...paresDeCampos(CAMPOS_FAMILIARES_016, estado.familiares, `${t('formularios.f016.familiaresTitulo')} — `));
  for (let i = 1; i <= NUM_HERMANOS_016; i++) {
    const h = estado.hermanos[String(i)];
    const partes = [h.tipoParto && `Tipo de parto: ${h.tipoParto}`, h.condicionParto && `Condición: ${h.condicionParto}`, h.edadActual && `Edad actual: ${h.edadActual}`, h.observaciones && `Observaciones: ${h.observaciones}`].filter(Boolean);
    pares.push({ pregunta: `${t('formularios.f016.hermanosTitulo')} — ${i}`, respuesta: partes.join(' · ') });
  }
  pares.push(...paresDeCampos(CAMPOS_OBSTETRICOS_MADRE_016, estado.obstetricosMadre, `${t('formularios.f016.obstetricosMadreTitulo')} — `));

  pares.push({ pregunta: t('formularios.f016.prenatalesTitulo'), respuesta: estado.antecedentesPrenatales.trim() });

  pares.push(...paresDeCampos(CAMPOS_NATALES_016, estado.natales, `${t('formularios.f016.natalesTitulo')} — `));
  for (const grupo of GRUPOS_SI_NO_016) {
    for (const item of grupo.items) pares.push({ pregunta: item.etiqueta, respuesta: etiquetaSiNo(estado.siNo[item.clave] ?? '') });
  }

  for (const minuto of MINUTOS_APGAR_016) {
    const titulo = `${t('formularios.f016.apgarTitulo')} — ${minuto} min`;
    for (const item of DEFINICION_APGAR_016.items) {
      const valor = estado.apgar[minuto][item.clave];
      pares.push({ pregunta: `${titulo} — ${item.etiqueta}`, respuesta: (() => { const o = item.opciones.find((x) => x.valor === valor); return o ? `${o.valor} — ${o.etiqueta}` : ''; })() });
    }
    const total = totalApgar016(estado.apgar, minuto);
    pares.push({ pregunta: `${titulo} — Total`, respuesta: total === undefined ? '' : String(total) });
  }

  pares.push(...paresDeFilasConColumnas(FILAS_CONSTANTES_016, COLUMNAS_CONSTANTES_016, estado.constantes, t('formularios.f016.constantesTitulo')));

  for (const s of SISTEMAS_EXAMEN_016) {
    const f = estado.sistemas[s.clave];
    const fmt = (estadoSistema: string, descripcion: string): string => {
      const e = estadoSistema.toUpperCase();
      return [e, descripcion.trim()].filter(Boolean).join(' — ');
    };
    pares.push({ pregunta: `${t('formularios.f016.examenFisicoTitulo')} — ${s.etiqueta} — Inicial`, respuesta: fmt(f.inicialEstado, f.inicialDescripcion) });
    pares.push({ pregunta: `${t('formularios.f016.examenFisicoTitulo')} — ${s.etiqueta} — Al egreso`, respuesta: fmt(f.egresoEstado, f.egresoDescripcion) });
  }
  pares.push(...paresDeCampos(CAMPOS_ANTROPOMETRIA_016, estado.antropometria));
  const perdida = porcentajePerdidaPeso016(estado.antropometria);
  if (perdida !== undefined) pares.push({ pregunta: t('formularios.f016.porcentajePerdidaPeso'), respuesta: `${perdida.toFixed(1)} %` });
  pares.push(...paresDeCampos(CAMPOS_SATURACION_016, estado.saturacion));

  estado.diagnosticosInicial.forEach((d, i) =>
    pares.push({ pregunta: `${t('formularios.f016.diagnosticoInicialTitulo')} — ${i + 1}`, respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF) }),
  );
  pares.push({ pregunta: t('formularios.f016.planInicialTitulo'), respuesta: estado.planInicial.trim() });

  pares.push({ pregunta: t('formularios.f016.clasificacionNeonato'), respuesta: estado.clasificacionNeonato.trim() });
  estado.diagnosticosEgreso.forEach((d, i) => pares.push({ pregunta: `${t('formularios.f016.diagnosticoEgresoTitulo')} — ${i + 1}`, respuesta: conCodigoCie(d) }));
  pares.push({ pregunta: t('formularios.f016.planEgresoTitulo'), respuesta: estado.planEgreso.trim() });

  pares.push(...paresProfesionalPorTurno(t, 'formularios.f016.profesionalTurnos', CAMPOS_PROFESIONAL_PAPEL, estado.profesional, TURNOS_PROFESIONAL_016));

  return pares.filter((p) => p.respuesta.trim() !== '');
}
